import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BeakerIcon, LockKeyhole, ArrowRight, AlertCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '@/config/config';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/config/routes';
import { useAuthTranslations } from '@/hooks/useAuthTranslations';
import { logDebug, logError } from '@/utils/errorHandler';

export default function SetPasswordPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const auth = useAuthTranslations();

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!fullName) {
      setError(auth.common.validation.enterFullName);
      return;
    }

    if (!password || password.length < 6) {
      setError(auth.common.validation.passwordMinLength);
      return;
    }

    if (password !== confirmPassword) {
      setError(auth.common.validation.passwordsDoNotMatch);
      return;
    }

    if (!email || !token || !orgId) {
      setError(auth.common.validation.missingRequiredInfo);
      return;
    }

    try {
      setLoading(true);

      logDebug('Setting password for user', {
        component: 'SetPasswordPage',
        action: 'handleSubmit.setPassword',
        metadata: { email, orgId, tokenPreview: token?.substring(0, 5) + '...' },
      });

      // Extract numeric ID from token if it's a UUID
      // If token is already numeric, this will work fine
      // If token is a UUID or other non-numeric string, we need to use a different endpoint

      // Use the /api/set-password/ endpoint instead which doesn't require a membership ID
      const response = await axios.post(`${API_URL}/api/set-password/`, {
        email,
        password,
        password_confirm: confirmPassword,
        organization_id: orgId,
        invitation_token: token,
        name: fullName,
      });

      logDebug('Password set successfully', {
        component: 'SetPasswordPage',
        action: 'handleSubmit.passwordSetSuccess',
        metadata: { email, orgId, hasTokens: !!(response.data.access && response.data.refresh) },
      });

      // If we got tokens back, log the user in
      if (response.data.access && response.data.refresh) {
        // Store tokens
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);

        // Try to log in with the new credentials
        try {
          await login(email, password);
          toast.success(auth.setPassword.success.setupComplete);
          navigate(ROUTES.APP.ROOT);
        } catch (loginError) {
          logError(
            'Auto-login failed after setting password',
            {
              component: 'SetPasswordPage',
              action: 'handleSubmit.autoLogin',
              metadata: { email, orgId, loginError },
            },
            { showToast: false, userFacing: false }
          );
          toast.success(auth.setPassword.success.setupCompleteLogin);
          navigate(ROUTES.AUTH.LOGIN);
        }
      } else {
        // No tokens returned, redirect to login
        toast.success(auth.setPassword.success.setupCompleteLogin);
        navigate(ROUTES.AUTH.LOGIN);
      }
    } catch (err: unknown) {
      logError(
        'Error setting up account',
        {
          component: 'SetPasswordPage',
          action: 'handleSubmit',
          metadata: { email, orgId, err },
        },
        { showToast: false, userFacing: false }
      );

      // Handle specific error messages from the API
      if (axios.isAxiosError(err) && err.response) {
        logError(
          'Server response error',
          {
            component: 'SetPasswordPage',
            action: 'handleSubmit.serverError',
            metadata: { email, orgId, responseData: err.response.data },
          },
          { showToast: false, userFacing: false }
        );

        if (typeof err.response.data === 'object') {
          const errorData = err.response.data;
          let errorMessage = auth.setPassword.errors.setupFailed;

          Object.keys(errorData).forEach(key => {
            const value = errorData[key];
            if (Array.isArray(value)) {
              errorMessage += `${key}: ${value.join(', ')}. `;
            } else if (typeof value === 'object' && value !== null) {
              errorMessage += `${key}: ${JSON.stringify(value)}. `;
            } else {
              errorMessage += `${key}: ${value}. `;
            }
          });

          setError(errorMessage);
        } else {
          setError(err.response.data || auth.common.errors.setupError);
        }
      } else {
        setError(auth.common.errors.unexpectedError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left Panel - Image */}
      <div className="relative hidden flex-1 lg:block">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-500 to-primary-700">
          <div className="absolute inset-0 bg-enterprise-900 opacity-20 mix-blend-multiply"></div>
          <div className="absolute inset-x-0 bottom-0 p-8 text-white">
            <h2 className="mb-4 text-2xl font-bold">{auth.setPassword.leftPanel.title}</h2>
            <p className="text-primary-100">{auth.setPassword.leftPanel.description}</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="order-first flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:order-last lg:max-w-md lg:flex-none lg:px-8 xl:max-w-lg">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-10">
            <div className="flex items-center">
              <BeakerIcon className="mr-2 size-7 text-primary-600" />
              <h1 className="text-2xl font-bold text-enterprise-900">
                {auth.common.branding.title}
              </h1>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-enterprise-900">
              {auth.setPassword.title}
            </h2>
            <p className="mt-2 text-sm text-enterprise-600">{auth.setPassword.subtitle}</p>
            {email && (
              <div className="mt-4 rounded-md border border-primary-100 bg-primary-50 p-3">
                <p className="text-sm text-primary-600">
                  {auth.setPassword.emailInfo} <strong>{email}</strong>
                </p>
              </div>
            )}
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="fullName" className="block text-sm font-medium text-enterprise-700">
                {auth.common.fields.fullName}
              </Label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="size-5 text-enterprise-400" />
                </div>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  required
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="block w-full border border-enterprise-200 pl-10 focus-visible:border-primary-500 focus-visible:ring-primary-500"
                  placeholder={auth.common.placeholders.fullName}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="block text-sm font-medium text-enterprise-700">
                {auth.common.fields.newPassword}
              </Label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <LockKeyhole className="size-5 text-enterprise-400" />
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="block w-full border border-enterprise-200 pl-10 focus-visible:border-primary-500 focus-visible:ring-primary-500"
                  placeholder={auth.common.placeholders.password}
                />
              </div>
              <p className="mt-1 text-xs text-enterprise-500">
                {auth.common.validation.minimumCharacters}
              </p>
            </div>

            <div>
              <Label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-enterprise-700"
              >
                {auth.common.fields.confirmPassword}
              </Label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <LockKeyhole className="size-5 text-enterprise-400" />
                </div>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="block w-full border border-enterprise-200 pl-10 focus-visible:border-primary-500 focus-visible:ring-primary-500"
                  placeholder={auth.common.placeholders.password}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-danger-50 p-4">
                <div className="flex">
                  <div className="shrink-0">
                    <AlertCircle className="size-5 text-danger-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-danger-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                variant="primary"
                fullWidth={true}
                isLoading={loading}
                loadingText={auth.setPassword.buttons.settingUp}
              >
                <div className="flex items-center">
                  {auth.setPassword.buttons.completeSetup}
                  <ArrowRight className="ml-2 size-4" />
                </div>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
